import { useRequiredContext } from "@redotech/react-util/context";
import { useLoad } from "@redotech/react-util/load";
import Copy01Icon from "@redotech/redo-icons/copy-01.svg";
import { CHARGEBACKS_SHOPIFY_APP_URL } from "@redotech/redo-partners-attribution/app-url";
import { toast } from "@redotech/redo-web/alert";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { RedoTextInput } from "@redotech/redo-web/arbiter-components/input/redo-text-input";
import {
  type BreadcrumbSlot,
  useBreadcrumb,
} from "@redotech/redo-web/breadcrumb";
import { Flex } from "@redotech/redo-web/flex";
import { ImageUpload } from "@redotech/redo-web/image-upload";
import { Text } from "@redotech/redo-web/text";
import classNames from "classnames";
import { memo, useCallback, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";

import type { PartnerDetail } from "@redotech/admin-sdk/rpc/schema/partners/shared";

import { RedoAdminRpcClientContext } from "../app/redo-admin-rpc-client-provider";
import { ClientUploader } from "../client/uploader";
import { DetailPageBackButton } from "../onboardings/components/DetailPageBackButton";
// Reuse the merchant detail page's layout CSS so the partner detail
// surface reads as the same kind of page (sticky white header with
// title + tags row, in-header tab strip). Partners only have one level
// of navigation, so the tab strip lives in the white header — not a grey
// bar below.
import * as shellStyles from "../onboardings/merchants/merchant-detail-page.module.css";
import { ImpersonatePartnerButton } from "./components/impersonate-partner-button";
import { PartnerActionPanel } from "./components/partner-action-panel";
import { PartnerAuthEventsList } from "./components/partner-auth-events-list";
import { PartnerInstallsList } from "./components/partner-installs-list";
import { PartnerStatusBadge } from "./components/partner-status-badge";
import { mapPartnerErrorToFieldError } from "./lib/map-partner-error-to-field-errors";

type TabKey = "overview" | "auth-events" | "installs" | "payouts";
const VALID_TABS: TabKey[] = ["overview", "auth-events", "installs", "payouts"];

interface PartnerDetailPageProps {
  breadcrumb: BreadcrumbSlot;
}

export const PartnerDetailPage = memo(function PartnerDetailPage({
  breadcrumb,
}: PartnerDetailPageProps) {
  const rpcClient = useRequiredContext(RedoAdminRpcClientContext);
  const navigate = useNavigate();
  const { partnerId } = useParams<{ partnerId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();

  const tab: TabKey = parseTab(searchParams.get("tab"));
  const setTab = useCallback(
    (next: TabKey) => {
      const params = new URLSearchParams(searchParams);
      if (next === "overview") {
        params.delete("tab");
      } else {
        params.set("tab", next);
      }
      setSearchParams(params, { replace: false });
    },
    [searchParams, setSearchParams],
  );

  const [refreshKey, setRefreshKey] = useState(0);
  const refresh = useCallback(() => setRefreshKey((k) => k + 1), []);

  const detailLoad = useLoad(
    async (signal) => {
      if (!partnerId) {
        return null;
      }
      return rpcClient.getPartner({ partnerId }, { signal });
    },
    [rpcClient, partnerId, refreshKey],
  );

  const handleBack = useCallback(() => navigate("/partners"), [navigate]);

  useBreadcrumb(breadcrumb, detailLoad.value?.partner?.name || "");

  if (detailLoad.pending && detailLoad.value == null) {
    return (
      <div className={shellStyles.pageWrapper}>
        <div className={shellStyles.loadingContainer}>Loading partner…</div>
      </div>
    );
  }
  if (detailLoad.error) {
    return (
      <div className={shellStyles.pageWrapper}>
        <div className={shellStyles.errorContainer}>
          <Text fontSize="md" textColor="error">
            Failed to load partner.
          </Text>
          <RedoButton
            hierarchy="secondary"
            onClick={refresh}
            size="sm"
            text="Retry"
          />
        </div>
      </div>
    );
  }
  const partner = detailLoad.value?.partner;
  if (!partner) {
    return (
      <div className={shellStyles.pageWrapper}>
        <div className={shellStyles.errorContainer}>Partner not found.</div>
      </div>
    );
  }

  const isInstallsTab = tab === "installs";

  return (
    <Flex
      className={shellStyles.pageWrapper}
      dir="column"
      gap="none"
      height={isInstallsTab ? "screen" : undefined}
      overflow={isInstallsTab ? "hidden" : undefined}
    >
      <div className={shellStyles.topHeader}>
        <DetailPageBackButton onClick={handleBack} />
        <div className={shellStyles.topHeaderContent}>
          <div className={shellStyles.topHeaderMain}>
            <div className={shellStyles.topHeaderLeft}>
              <div className={shellStyles.headerTags}>
                <PartnerStatusBadge status={partner.status} />
              </div>
            </div>
            <div className={shellStyles.headerActions}>
              <ImpersonatePartnerButton partner={partner} />
              <PartnerActionPanel
                onPartnerChanged={refresh}
                partner={partner}
              />
            </div>
          </div>
          <div className={shellStyles.productNavBar}>
            {VALID_TABS.map((t) => (
              <Flex
                as="button"
                className={classNames(
                  shellStyles.productTab,
                  tab === t && shellStyles.productTabActive,
                )}
                gap="xs"
                key={t}
                onClick={() => setTab(t)}
                type="button"
              >
                {TAB_LABELS[t]}
              </Flex>
            ))}
          </div>
        </div>
      </div>
      {isInstallsTab ? (
        <Flex dir="column" flex={1} gap="xl" overflow="hidden" p="2xl" w="full">
          <PartnerInstallsList
            installCount={partner.installCount}
            partnerId={partner.id}
          />
        </Flex>
      ) : (
        <div className={shellStyles.pageContainer}>
          {tab === "overview" && (
            <OverviewTab onPartnerChanged={refresh} partner={partner} />
          )}
          {tab === "auth-events" && (
            <PartnerAuthEventsList events={partner.recentAuthEvents} />
          )}
          {tab === "payouts" && (
            <DeferredTab description="Stripe payouts land in PR10." />
          )}
        </div>
      )}
    </Flex>
  );
});

const TAB_LABELS: Record<TabKey, string> = {
  overview: "Overview",
  "auth-events": "Auth events",
  installs: "Installs",
  payouts: "Payouts",
};

function parseTab(raw: string | null): TabKey {
  if (raw && (VALID_TABS as string[]).includes(raw)) {
    return raw as TabKey;
  }
  return "overview";
}

interface EditableNameRowProps {
  partner: PartnerDetail;
  onUpdated: () => void;
}

const EditableNameRow = memo(function EditableNameRow({
  partner,
  onUpdated,
}: EditableNameRowProps) {
  const rpcClient = useRequiredContext(RedoAdminRpcClientContext);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(partner.name);
  const [submitting, setSubmitting] = useState(false);

  const cancel = useCallback(() => {
    setDraft(partner.name);
    setEditing(false);
  }, [partner.name]);

  const save = useCallback(async () => {
    const trimmed = draft.trim();
    if (trimmed.length === 0) {
      toast("Name cannot be blank.", { variant: "error" });
      return;
    }
    if (trimmed === partner.name) {
      setEditing(false);
      return;
    }
    setSubmitting(true);
    try {
      await rpcClient.updatePartner({ partnerId: partner.id, name: trimmed });
      toast("Name updated.", { variant: "success" });
      setEditing(false);
      onUpdated();
    } catch (err) {
      const mapping = mapPartnerErrorToFieldError(err);
      toast(
        mapping?.message ??
          (err instanceof Error ? err.message : "Failed to update name."),
        { variant: "error" },
      );
    } finally {
      setSubmitting(false);
    }
  }, [draft, partner.id, partner.name, rpcClient, onUpdated]);

  return (
    <Flex align="center" dir="row" gap="md">
      <Text fontSize="sm" fontWeight="semibold">
        Name
      </Text>
      {editing ? (
        <>
          <RedoTextInput
            placeholder="Partner name"
            setValue={setDraft}
            value={draft}
          />
          <RedoButton
            disabled={submitting}
            hierarchy="primary"
            onClick={() => void save()}
            size="xs"
            text={submitting ? "Saving…" : "Save"}
          />
          <RedoButton
            disabled={submitting}
            hierarchy="tertiary"
            onClick={cancel}
            size="xs"
            text="Cancel"
          />
        </>
      ) : (
        <>
          <Text fontSize="sm">{partner.name}</Text>
          <RedoButton
            hierarchy="tertiary"
            onClick={() => setEditing(true)}
            size="xs"
            text="Edit"
          />
        </>
      )}
    </Flex>
  );
});

interface OverviewTabProps {
  partner: PartnerDetail;
  onPartnerChanged: () => void;
}

const OverviewTab = memo(function OverviewTab({
  partner,
  onPartnerChanged,
}: OverviewTabProps) {
  const copyAppLink = useCallback(() => {
    void navigator.clipboard.writeText(CHARGEBACKS_SHOPIFY_APP_URL).then(
      () => {
        toast("Listing link copied.", { variant: "success" });
      },
      () => {
        toast("Couldn't copy listing link.", { variant: "error" });
      },
    );
  }, []);

  return (
    <div className={shellStyles.tabContent}>
      <div className={shellStyles.metaSection}>
        <EditableNameRow onUpdated={onPartnerChanged} partner={partner} />
        <PartnerProfileImageSection
          key={`${partner.id}-${partner.updatedAt}-${partner.profileImageUrl ?? ""}`}
          onUpdated={onPartnerChanged}
          partner={partner}
        />
        <MetadataRow label="Partner ID" value={partner.id} />
        <MetadataRow label="Email" value={partner.email} />
        <MetadataRow
          label="Partner type"
          value={partnerTypeLabel(partner.partnerType)}
        />
        <MetadataRow label="Referral code" value={partner.referralCode} />
        <MetadataRow
          label="Attributed installs"
          value={partner.installCount.toLocaleString()}
        />
        <Flex align="center" dir="row" gap="md">
          <Text fontSize="sm" fontWeight="semibold">
            Chargebacks listing
          </Text>
          <Text fontSize="sm">{CHARGEBACKS_SHOPIFY_APP_URL}</Text>
          <RedoButton
            hierarchy="tertiary"
            IconLeading={Copy01Icon}
            onClick={copyAppLink}
            size="xs"
            text="Copy"
          />
        </Flex>
        <Text color="subdued" fontSize="sm">
          Referral credit will come from merchant code entry during onboarding.
        </Text>
        <MetadataRow label="Created by" value={partner.createdBy} />
        <MetadataRow
          label="Created"
          value={new Date(partner.createdAt).toLocaleString()}
        />
        <MetadataRow
          label="Last updated"
          value={new Date(partner.updatedAt).toLocaleString()}
        />
      </div>
    </div>
  );
});

interface PartnerProfileImageSectionProps {
  partner: PartnerDetail;
  onUpdated: () => void;
}

const PartnerProfileImageSection = memo(function PartnerProfileImageSection({
  partner,
  onUpdated,
}: PartnerProfileImageSectionProps) {
  const rpcClient = useRequiredContext(RedoAdminRpcClientContext);
  const [profileImageUrl, setProfileImageUrl] = useState(
    partner.profileImageUrl,
  );
  const [submitting, setSubmitting] = useState(false);

  const save = useCallback(
    async (nextValue: URL | undefined) => {
      const previousValue = profileImageUrl;
      const nextProfileImageUrl = nextValue?.toString() ?? null;
      setProfileImageUrl(nextProfileImageUrl);
      setSubmitting(true);
      try {
        await rpcClient.updatePartner({
          partnerId: partner.id,
          profileImageUrl: nextProfileImageUrl,
        });
        toast(
          nextProfileImageUrl
            ? "Profile image updated."
            : "Profile image removed.",
          { variant: "success" },
        );
        onUpdated();
      } catch (err) {
        setProfileImageUrl(previousValue);
        toast(err instanceof Error ? err.message : "Failed to update image.", {
          variant: "error",
        });
      } finally {
        setSubmitting(false);
      }
    },
    [onUpdated, partner.id, profileImageUrl, rpcClient],
  );

  return (
    <Flex align="flex-start" dir="column" gap="md">
      <Flex dir="column" gap="xs" maxW="sm">
        <Text fontSize="md" fontWeight="semibold">
          Partner profile picture
        </Text>
        <Text fontSize="sm" textColor="secondary">
          Used for this partner account in admin and the partner portal.
        </Text>
      </Flex>
      <ClientUploader>
        <Flex grow={0} height="fit-content" w="xs">
          <ImageUpload
            accept={["image/png", "image/jpeg", "image/webp", "image/gif"]}
            caption="PNG, JPG, WebP, or GIF."
            disabled={submitting}
            onChange={(nextValue) => void save(nextValue)}
            showErrorMessages
            value={toOptionalUrl(profileImageUrl)}
          />
        </Flex>
      </ClientUploader>
    </Flex>
  );
});

function toOptionalUrl(value: string | null): URL | undefined {
  if (!value) {
    return undefined;
  }
  try {
    return new URL(value);
  } catch {
    return undefined;
  }
}

function MetadataRow({ label, value }: { label: string; value: string }) {
  return (
    <Flex align="center" dir="row" gap="md">
      <Text fontSize="sm" fontWeight="semibold">
        {label}
      </Text>
      <Text fontSize="sm">{value}</Text>
    </Flex>
  );
}

function partnerTypeLabel(type: string): string {
  switch (type) {
    case "individual":
      return "Individual";
    case "app":
      return "App";
    case "agency":
      return "Agency";
    default:
      return type;
  }
}

function DeferredTab({ description }: { description: string }) {
  return (
    <Flex align="center" justify="center" p="3xl">
      <Text fontSize="md" textColor="secondary">
        {description}
      </Text>
    </Flex>
  );
}
